import {
  MinusCircle,
  Plus,
  Edit,
  ChevronDown,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  ChevronLeft,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AddTask from "./add-task";
import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import {
  ALLOWED_COVER_IMAGE_TYPES,
  validateFileSize,
  validateFileType,
} from "../../config/fileConfig";
import Chat from "./chat";

const createRoleId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `role_${Math.random().toString(36).slice(2, 10)}`;
};

export default function MainNewRequest({ requestId }) {
  const [searchParams] = useSearchParams();
  const recipientIdFromQuery = searchParams.get("recipientId");
  const [isOpen, setIsOpen] = useState(false);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // const [previewImageError, setPreviewImageError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const navigate = useNavigate();

  // Event editing state (only for "My Requests")
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    categoryValue: "",
    description: "",
    date: "",
    time: "",
    location: "",
    resourceRequirements: "",
    inviteNgoMunicipality: false,
  });
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [isUploadingCoverImage, setIsUploadingCoverImage] = useState(false);

  // Resources state management (similar to roles in post-event)
  const [resources, setResources] = useState([]);
  const [files, setFiles] = useState([]);
  const [resourceDraft, setResourceDraft] = useState({
    roleName: "",
    countNeeded: "",
    quantityType: "Unit",
    specialRequirements: "",
  });

  useEffect(() => {
    if (requestId) {
      fetchRequestData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, recipientIdFromQuery]);

  // Fetch user services (categories) for display
  useEffect(() => {
    const fetchUserServices = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      setIsLoadingCategories(true);
      try {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.GET_USER_SERVICES}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          const result = await response.json();
          const services = result?.data || result;
          setCategories(Array.isArray(services) ? services : []);
        } else {
          console.error("Failed to fetch user services");
        }
      } catch (error) {
        console.error("Error fetching user services:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchUserServices();
  }, []);

  const fetchRequestData = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_COLLABORATION_REQUEST}/${requestId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to fetch request details");
      }

      const data = result?.data || result;
      setRequestData(data);

      // Load resources/roles from the collaboration request response
      // The backend now includes roles in the event object for all tabs
      if (data?.event?.roles && Array.isArray(data.event.roles)) {
        setResources(
          data.event.roles.map((role) => ({
            id: role.id || createRoleId(),
            roleName: role.roleName,
            countNeeded: role.countNeeded,
            quantityType: role.quantityType || "Unit",
            specialRequirements: role.specialRequirements || "",
          })),
        );
      } else {
        // If no roles found, clear resources to show empty state
        setResources([]);
      }

      // Initialize event form state if coming from "My Requests"
      if (recipientIdFromQuery && data?.event) {
        setEventForm({
          title: data.event.title || "",
          categoryValue: data.event.categoryValue || data.event.typeCode || "",
          description: data.event.description || "",
          date: data.event.date || "",
          time: data.event.time || "",
          location: data.event.location || "",
          resourceRequirements: data.event.resourceRequirements || "",
          inviteNgoMunicipality: data.event.inviteNgoMunicipality || false,
        });
        setCoverImageUrl(data.event.coverImageUrl || null);
        setCoverImage(null); // Reset cover image file
      }

      // Load event attachments if available
      if (data?.event?.attachments && Array.isArray(data.event.attachments)) {
        const normalizedAttachments = data.event.attachments
          .map((att) => {
            if (typeof att === "object" && att !== null && att.url) {
              return {
                url: att.url,
                name: att.name || att.url.split("/").pop() || "file",
              };
            }
            if (typeof att === "string") {
              return {
                url: att,
                name: att.split("/").pop() || "file",
              };
            }
            return null;
          })
          .filter(Boolean);
        setFiles(normalizedAttachments);
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error("Fetch request error:", error);
      toast.error(error?.message || "Failed to load request details");
    } finally {
      setIsLoading(false);
    }
  };

  // Use actual data if available, otherwise use placeholder
  const event = requestData?.event;
  const fromNonprofit = requestData?.fromNonprofit;

  // useEffect(() => {
  //   // Reset error state when event or coverImageUrl changes
  //   if (event?.coverImageUrl) {
  //     setPreviewImageError(false);
  //   }
  // }, [event?.coverImageUrl]);

  // Get current user's nonprofit ID and organization name
  const [currentUserNonprofitId, setCurrentUserNonprofitId] = useState(null);
  const [currentUserOrganizationName, setCurrentUserOrganizationName] =
    useState(null);

  useEffect(() => {
    const fetchCurrentUserNonprofit = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      try {
        const response = await fetch(`${BASE_URL}users/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json().catch(() => null);
        const statusCode = result?.statusCode ?? response.status;

        if (response.ok && statusCode < 400 && result?.data?.nonProfitUser) {
          setCurrentUserNonprofitId(result.data.nonProfitUser.id);
          setCurrentUserOrganizationName(
            result.data.nonProfitUser.organizationName,
          );
        }
      } catch (error) {
        console.error("Fetch current user nonprofit error:", error);
      }
    };

    if (requestId) {
      fetchCurrentUserNonprofit();
    }
  }, [requestId]);

  // Determine which recipient to show:
  // 1. If recipientId is in query params (from "My Requests" tile click), use that
  // 2. If current user is a recipient (Received/Accepted/Declined tabs), show that recipient
  // 3. Otherwise, show the first recipient (for "My Requests" or if user is the sender)
  const recipient = recipientIdFromQuery
    ? requestData?.recipients?.find((r) => r.id === recipientIdFromQuery)
    : requestData?.recipients?.find(
        (r) => r.nonprofit?.id === currentUserNonprofitId,
      ) || requestData?.recipients?.[0];

  const tasks = recipient?.tasks || [];

  // Resource management handlers
  const handleResourceDraftChange = (field) => (event) => {
    setResourceDraft((prev) => ({
      ...prev,
      [field]:
        field === "countNeeded"
          ? event.target.value.replace(/\D/g, "")
          : event.target.value,
    }));
  };

  const handleAddResource = () => {
    if (!resourceDraft.countNeeded || !resourceDraft.roleName) {
      toast.error("Please fill in type and quantity");
      return;
    }

    setResources((prev) => [
      ...prev,
      {
        id: createRoleId(),
        roleName: resourceDraft.roleName,
        countNeeded: Number(resourceDraft.countNeeded),
        quantityType: resourceDraft.quantityType || "Unit",
        specialRequirements: resourceDraft.specialRequirements.trim(),
      },
    ]);

    setResourceDraft((prev) => ({
      ...prev,
      countNeeded: "",
      specialRequirements: "",
    }));
  };

  const handleRemoveResource = (id) => {
    setResources((prev) => prev.filter((resource) => resource.id !== id));
  };

  // const handleResourceFiles = (selectedFiles) => {
  //   const fileArray = Array.from(selectedFiles || []);
  //   setFiles(fileArray);
  // };

  // Event form handlers (only for "My Requests")
  const handleEventFormChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setEventForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCoverImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!validateFileType(file, ALLOWED_COVER_IMAGE_TYPES)) {
      toast.error(
        `Invalid file type. Allowed types: ${ALLOWED_COVER_IMAGE_TYPES.join(
          ", ",
        )}`,
      );
      return;
    }

    // Validate file size (5MB limit)
    if (!validateFileSize(file, 5 * 1024 * 1024)) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setCoverImage(file);
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImageUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadCoverImage = async () => {
    if (coverImage instanceof File) {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Session expired. Please sign in again.");
      }

      setIsUploadingCoverImage(true);
      try {
        const formData = new FormData();
        formData.append("file", coverImage);

        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const result = await response.json().catch(() => null);
        const statusCode = result?.statusCode ?? response.status;

        if (!response.ok || statusCode >= 400) {
          const message = result?.message || "Failed to upload cover image";
          throw new Error(message);
        }

        const uploadedUrl = result?.data?.url || result?.url;
        if (uploadedUrl) {
          setCoverImageUrl(uploadedUrl);
          return uploadedUrl;
        } else {
          throw new Error("No URL returned from upload");
        }
      } catch (error) {
        console.error("Cover image upload error:", error);
        throw error;
      } finally {
        setIsUploadingCoverImage(false);
      }
    }

    // If coverImageUrl is already a Cloudinary URL, return it
    if (
      coverImageUrl &&
      typeof coverImageUrl === "string" &&
      coverImageUrl.startsWith("http")
    ) {
      return coverImageUrl;
    }

    return undefined;
  };

  const handleSaveEventDetails = async () => {
    if (!event?.id) {
      toast.error("Event ID not found");
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      navigate("/");
      return;
    }

    setIsSavingEvent(true);
    try {
      // Upload cover image if there's a new file
      const uploadedCoverImageUrl = await uploadCoverImage();

      // Prepare attachments array (existing attachments)
      const attachments = Array.isArray(event.attachments)
        ? event.attachments.map((url) =>
            typeof url === "string" ? url : url.name || url,
          )
        : [];

      // Update event with new details
      const payload = {
        eventId: event.id,
        title: eventForm.title?.trim() ?? "",
        categoryValue:
          eventForm.categoryValue || event?.categoryValue || event?.typeCode,
        description: eventForm.description?.trim() || undefined,
        date: eventForm.date,
        time: eventForm.time,
        location: eventForm.location?.trim() ?? "",
        resourceRequirements:
          eventForm.resourceRequirements?.trim() || undefined,
        needVolunteers: event.needVolunteers || resources.length > 0,
        inviteNgoMunicipality: eventForm.inviteNgoMunicipality,
        roles: resources.map(
          ({ roleName, countNeeded, specialRequirements }) => ({
            roleName,
            countNeeded: Number(countNeeded) || 0,
            specialRequirements: specialRequirements || undefined,
          }),
        ),
        attachments,
        coverImageUrl:
          uploadedCoverImageUrl || event.coverImageUrl || undefined,
        isDraft: event.isDraft || false,
      };

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_UPDATE_EVENT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to save event details");
      }

      toast.success("Event details saved successfully");
      setIsEditingEvent(false);
      // Refresh request data
      fetchRequestData();
      // Clear cover image file after successful save
      setCoverImage(null);
    } catch (error) {
      console.error("Save event details error:", error);
      toast.error(error?.message || "Failed to save event details");
    } finally {
      setIsSavingEvent(false);
    }
  };

  const roleNameDisplay = useMemo(() => {
    const displayMap = categories.reduce((acc, category) => {
      acc[category.value] = category.name;
      return acc;
    }, {});
    // Helper function to get display name
    return (roleName) => {
      return displayMap[roleName] || "Other";
    };
  }, [categories]);

  // Save resources to event
  const handleSaveResources = async () => {
    if (!event?.id) {
      toast.error("Event ID not found");
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      navigate("/");
      return;
    }

    setIsSaving(true);
    try {
      // Upload files first if any
      const uploadedFileUrls = [];
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);

          const uploadResponse = await fetch(
            `${BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: formData,
            },
          );

          const uploadResult = await uploadResponse.json().catch(() => null);
          if (uploadResponse.ok && uploadResult?.data?.url) {
            uploadedFileUrls.push(uploadResult.data.url);
          }
        }
      }

      // Prepare attachments array (existing attachments + new ones)
      const attachments = Array.isArray(event.attachments)
        ? [...event.attachments, ...uploadedFileUrls]
        : uploadedFileUrls;

      // Update event with resources/roles - matching the structure from edit-event.jsx
      const payload = {
        eventId: event.id,
        title: event.title,
        categoryValue: event.categoryValue || event.typeCode,
        description: event.description,
        date: event.date,
        time: event.time,
        location: event.location,
        resourceRequirements: event.resourceRequirements,
        needVolunteers: event.needVolunteers || resources.length > 0, // Set to true if we have resources
        inviteNgoMunicipality: event.inviteNgoMunicipality,
        roles: resources.map(
          ({ roleName, countNeeded, specialRequirements }) => ({
            roleName,
            countNeeded: Number(countNeeded) || 0,
            specialRequirements: specialRequirements || undefined,
          }),
        ),
        attachments: attachments.map((url) =>
          typeof url === "string" ? url : url.name || url,
        ),
        coverImageUrl: event.coverImageUrl || undefined,
        isDraft: event.isDraft || false,
      };

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_UPDATE_EVENT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to save resources");
      }

      toast.success("Resources saved successfully");
      // Refresh request data
      fetchRequestData();
      // Clear files after successful save
      setFiles([]);
      // Navigate back to collaboration workspace
      navigate("/dashboard/collaboration-workspace");
    } catch (error) {
      console.error("Save resources error:", error);
      toast.error(error?.message || "Failed to save resources");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <p className="text-center text-gray-500">Loading request details...</p>
      </div>
    );
  }

  if (requestId && !requestData) {
    return (
      <div className="w-full">
        <p className="text-center text-red-500">Request not found</p>
        <Link to="/dashboard/collaboration-workspace">
          <button className="mt-4 px-4 py-2 bg-themeColor text-white rounded-md">
            Back to Collaboration Workspace
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row lg:justify-between mb-6">
      <div className="w-full lg:w-[59%]">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="">
            <h1 className="font-popinsBold text-xl lg:text-2xl">
              {event?.title || "Co-Hosted Event"}
            </h1>
            <p className="font-popinsRegular text-black/50 text-sm mt-2">
              {fromNonprofit?.organizationName
                ? `Hosted by ${fromNonprofit.organizationName}`
                : "Collaboration Request"}
            </p>
            <Link to={"/dashboard/collaboration-workspace"}>
              <button
                className="relative overflow-hidden py-1 px-3 border border-gray-300 text-gray-500 font-popinsRegular text-xs rounded
                             transform transition duration-300 ease-in-out
                             hover:border-gray-400 hover:text-gray-600 hover:bg-gray-50
                             active:scale-95 focus:outline-none
                             flex items-center gap-x-1.5 mt-4"
              >
                <ChevronLeft size={12} />
                <span>Back to Collaboration Workspace</span>
              </button>
            </Link>
          </div>
          {event && recipientIdFromQuery && !isEditingEvent && (
            <button
              type="button"
              onClick={() => setIsEditingEvent(true)}
              className="relative overflow-hidden py-2 px-4 bg-themeColor text-white font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 active:scale-95 focus:outline-none flex items-center gap-x-2 whitespace-nowrap"
            >
              <Edit size={16} />
              <span>Edit Event Details</span>
            </button>
          )}
        </div>
        {event && (
          <div className="w-full mt-6">
            <form className="w-full">
              <div className="flex flex-col lg:flex-row lg:justify-between">
                <div className="w-full lg:w-[49%]">
                  <label className="font-popinsMedium">Event Title</label>
                  {isEditingEvent && recipientIdFromQuery ? (
                    <input
                      type="text"
                      placeholder="Event Title"
                      className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                      value={eventForm.title}
                      onChange={handleEventFormChange("title")}
                    />
                  ) : (
                    <p className="font-popinsRegular text-black/50 mt-2">
                      {event?.title || "N/A"}
                    </p>
                  )}
                </div>
                <div className="w-full mt-4 lg:mt-0 lg:w-[49%]">
                  <label className="font-popinsMedium">Category</label>
                  {isEditingEvent && recipientIdFromQuery ? (
                    <div className="relative">
                      <select
                        className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-gray-100 px-4 py-2 pr-10 outline-none cursor-not-allowed text-black/60 appearance-none"
                        value={
                          eventForm.categoryValue ||
                          event?.categoryValue ||
                          event?.typeCode ||
                          ""
                        }
                        disabled
                      >
                        <option value="" disabled className="text-gray-400">
                          {isLoadingCategories ? "Loading..." : "Category"}
                        </option>
                        {categories.map((category) => (
                          <option
                            key={category.id}
                            value={category.value}
                            className="text-black/80"
                          >
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-5 text-black/50 pointer-events-none">
                        <ChevronDown size={20} />
                      </div>
                    </div>
                  ) : (
                    <p className="font-popinsRegular text-black/50 mt-2">
                      {(() => {
                        const categoryValue =
                          event?.categoryValue || event?.typeCode;
                        if (!categoryValue) return "N/A";
                        const category = categories.find(
                          (cat) => cat.value === categoryValue,
                        );
                        return (
                          category?.name ||
                          event?.typeName ||
                          categoryValue ||
                          "N/A"
                        );
                      })()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col lg:flex-row lg:justify-between mt-4">
                <div className="w-full lg:w-[49%]">
                  <label className="font-popinsMedium">Date</label>
                  {isEditingEvent && recipientIdFromQuery ? (
                    <input
                      type="date"
                      className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer"
                      value={eventForm.date}
                      onChange={handleEventFormChange("date")}
                    />
                  ) : (
                    <p className="font-popinsRegular text-black/50 mt-2">
                      {event?.date || "N/A"}
                    </p>
                  )}
                </div>
                <div className="w-full mt-4 lg:mt-0 lg:w-[49%]">
                  <label className="font-popinsMedium">Time</label>
                  {isEditingEvent && recipientIdFromQuery ? (
                    <input
                      type="time"
                      className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer"
                      value={eventForm.time}
                      onChange={handleEventFormChange("time")}
                    />
                  ) : (
                    <p className="font-popinsRegular text-black/50 mt-2">
                      {event?.time || "N/A"}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col lg:flex-row lg:justify-between mt-4">
                <div className="w-full lg:w-[49%]">
                  <label className="font-popinsMedium">Description</label>
                  {isEditingEvent && recipientIdFromQuery ? (
                    <textarea
                      rows="4"
                      placeholder="Event Description"
                      className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] resize-none"
                      value={eventForm.description}
                      onChange={handleEventFormChange("description")}
                    />
                  ) : (
                    <p className="font-popinsRegular text-black/50 mt-2">
                      {event?.description || "No description provided"}
                    </p>
                  )}
                </div>
                <div className="w-full lg:w-[49%]">
                  <label className="font-popinsMedium">Location</label>
                  {isEditingEvent && recipientIdFromQuery ? (
                    <input
                      type="text"
                      placeholder="Event Location"
                      className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                      value={eventForm.location}
                      onChange={handleEventFormChange("location")}
                    />
                  ) : (
                    <p className="font-popinsRegular text-black/50 mt-2">
                      {event?.location || "N/A"}
                    </p>
                  )}
                </div>
              </div>
              {isEditingEvent && recipientIdFromQuery && (
                <div className="mt-4">
                  <label className="font-popinsMedium">
                    Resource Requirements
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Additional resource requirements"
                    className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] resize-none"
                    value={eventForm.resourceRequirements}
                    onChange={handleEventFormChange("resourceRequirements")}
                  />
                </div>
              )}
              {isEditingEvent && recipientIdFromQuery && (
                <div className="mt-4">
                  <label className="font-popinsMedium">Cover Image</label>
                  <div className="mt-2">
                    {coverImageUrl && (
                      <div className="mb-2">
                        <img
                          src={coverImageUrl}
                          alt="Cover preview"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <label
                      htmlFor="coverImageInput"
                      className="cursor-pointer inline-block bg-gray-100 hover:bg-[#3FC4E3] hover:text-white transition-all duration-300 ease-in-out text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
                    >
                      {coverImage ? "Change Image" : "Upload Cover Image"}
                    </label>
                    <input
                      id="coverImageInput"
                      type="file"
                      accept={ALLOWED_COVER_IMAGE_TYPES.join(",")}
                      className="hidden"
                      onChange={handleCoverImageChange}
                    />
                  </div>
                </div>
              )}
              {isEditingEvent && recipientIdFromQuery && (
                <div className="flex gap-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleSaveEventDetails}
                    disabled={isSavingEvent || isUploadingCoverImage}
                    className="relative overflow-hidden py-2 px-5 bg-themeColor text-white font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 active:scale-95 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSavingEvent || isUploadingCoverImage
                      ? "Saving..."
                      : "Save Event Details"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingEvent(false);
                      // Reset form to original event data
                      if (event) {
                        setEventForm({
                          title: event.title || "",
                          categoryValue:
                            event.categoryValue || event.typeCode || "",
                          description: event.description || "",
                          date: event.date || "",
                          time: event.time || "",
                          location: event.location || "",
                          resourceRequirements:
                            event.resourceRequirements || "",
                          inviteNgoMunicipality:
                            event.inviteNgoMunicipality || false,
                        });
                        setCoverImageUrl(event.coverImageUrl || null);
                        setCoverImage(null);
                      }
                    }}
                    className="relative overflow-hidden py-2 px-5 bg-gray-200 text-black font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-gray-300 hover:scale-105 active:scale-95 focus:outline-none"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
        {/* Resources section - always visible */}
        <div className="mt-8">
          <p className="font-popinsSemiBold mb-4 text-xl">Resources</p>

          {/* Only show Add Resource form when coming from "My Requests" tab (recipientIdFromQuery exists) */}
          {recipientIdFromQuery && (
            <>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="w-full lg:flex-[3]">
                  <div className="relative">
                    <select
                      className={`w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] cursor-pointer appearance-none ${
                        resourceDraft.roleName
                          ? "text-gray-900"
                          : "text-gray-400"
                      }`}
                      value={resourceDraft.roleName}
                      onChange={handleResourceDraftChange("roleName")}
                      disabled={isLoadingCategories}
                    >
                      <option value="" disabled className="text-gray-400">
                        {isLoadingCategories ? "Loading..." : "Select Type"}
                      </option>
                      {categories.map((category) => (
                        <option
                          key={category.id}
                          value={category.value}
                          className="text-gray-900"
                        >
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <ChevronDown size={18} />
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-auto lg:min-w-[200px]">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Qty."
                      className="w-20 font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)]"
                      value={resourceDraft.countNeeded}
                      onChange={handleResourceDraftChange("countNeeded")}
                      inputMode="numeric"
                    />
                    <div className="relative w-28">
                      <select
                        className="w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-3 py-2.5 pr-8 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] cursor-pointer appearance-none text-gray-900"
                        value={resourceDraft.quantityType}
                        onChange={handleResourceDraftChange("quantityType")}
                      >
                        <option value="Unit">Unit(s)</option>
                        <option value="Person">Person(s)</option>
                      </select>
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <ChevronDown size={18} />
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="relative overflow-hidden py-2.5 px-6 bg-themeColor text-white font-popinsSemiBold text-sm rounded-lg shadow-md transform transition-all duration-200 ease-in-out hover:bg-[#35b3d1] hover:shadow-lg active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#3FC4E3] focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-md whitespace-nowrap"
                  onClick={handleAddResource}
                  disabled={
                    isSaving ||
                    !resourceDraft.countNeeded ||
                    !resourceDraft.roleName
                  }
                >
                  <Plus size={16} strokeWidth={2.5} className="text-white" />
                  <span>Add</span>
                </button>
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm text-gray-700 mb-1.5 block">
                  Special Requirements
                </label>
                <textarea
                  rows="4"
                  className="w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] resize-none"
                  value={resourceDraft.specialRequirements}
                  onChange={handleResourceDraftChange("specialRequirements")}
                  placeholder="Add more details to your resource requirements"
                ></textarea>
              </div>
            </>
          )}

          {/* Resources grid - always visible */}
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm mt-5">
            <div className="flex border-b border-gray-200 bg-gray-50 px-5 py-3.5">
              <div className="w-[20%]">
                <p className="font-popinsSemiBold text-sm text-gray-700">
                  Type
                </p>
              </div>
              <div className="w-[20%]">
                <p className="font-popinsSemiBold text-sm text-gray-700">
                  Quantity
                </p>
              </div>
              <div className="w-[60%] flex justify-between items-center pr-4">
                <p className="font-popinsSemiBold text-sm text-gray-700">
                  Special Requirements
                </p>
                {recipientIdFromQuery && (
                  <span className="sr-only">Actions</span>
                )}
              </div>
            </div>
            {resources.length === 0 ? (
              <div className="py-8 text-center text-gray-400 font-popinsRegular text-sm">
                No volunteer roles added yet.
              </div>
            ) : (
              resources.map((resource, index) => (
                <div
                  key={resource.id}
                  className={`flex border-b last:border-b-0 border-gray-100 text-gray-700 py-4 px-5 items-center hover:bg-gray-50 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
                >
                  <div className="w-[20%]">
                    <p className="font-popinsMedium text-sm text-gray-900">
                      {roleNameDisplay(resource.roleName)}
                    </p>
                  </div>
                  <div className="w-[20%]">
                    <p className="font-popinsMedium text-sm text-gray-900">
                      {resource.countNeeded}{" "}
                      {resource.quantityType === "Person"
                        ? "Person(s)"
                        : "Unit(s)"}
                    </p>
                  </div>
                  <div className="w-[60%] flex justify-between items-center pr-2 gap-3">
                    <p className="font-popinsRegular text-sm text-gray-600 text-left break-words flex-1">
                      {resource.specialRequirements ? (
                        resource.specialRequirements
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </p>
                    {/* Only show remove button when coming from "My Requests" */}
                    {recipientIdFromQuery && (
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded hover:bg-red-50 flex-shrink-0"
                        onClick={() => handleRemoveResource(resource.id)}
                        title="Remove resource"
                        disabled={isSaving}
                      >
                        <MinusCircle size={18} aria-hidden />
                        <span className="sr-only">Remove resource</span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Supporting Files Section */}
        <div className="w-full mt-5">
          <p className="text-base mb-2 font-popinsSemiBold">Supporting Files</p>
          {files.length === 0 ? (
            <p className="text-sm text-gray-500 font-popinsRegular mt-2">
              No supporting files
            </p>
          ) : (
            <div className="mt-2 space-y-2">
              {files.map((file, index) => {
                // Extract filename from URL if name is still a URL
                let displayName = file.name;
                if (displayName && displayName.startsWith("http")) {
                  displayName =
                    displayName.split("/").pop() || `file-${index + 1}`;
                }
                // Clean up filename (remove query params, etc.)
                displayName = displayName.split("?")[0];

                // Determine file icon component based on file type
                const getFileIcon = (fileName) => {
                  const ext = fileName.split(".").pop()?.toLowerCase();
                  if (ext === "pdf") {
                    return <FileText size={18} className="text-red-600" />;
                  }
                  if (["doc", "docx"].includes(ext)) {
                    return <FileText size={18} className="text-blue-600" />;
                  }
                  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
                    return <ImageIcon size={18} className="text-green-600" />;
                  }
                  return <FileIcon size={18} className="text-gray-600" />;
                };

                const handleDownload = async (e) => {
                  e.preventDefault();
                  try {
                    const response = await fetch(file.url);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = displayName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error("Error downloading file:", error);
                    toast.error("Failed to download file. Please try again.");
                  }
                };

                return (
                  <div
                    key={`file-${index}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getFileIcon(displayName)}
                      </div>
                      <span
                        className="text-sm text-gray-700 truncate flex-1 font-popinsRegular"
                        title={displayName}
                      >
                        {displayName}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-themeColor text-white rounded-md hover:bg-[#3FC4E3] transition-colors text-xs font-popinsMedium"
                      title={`Download ${displayName}`}
                    >
                      <Download size={14} />
                      <span>Download</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* <div className="w-full h-[300px] mt-5">
          <img
            src="/assets/images/manage-event/map.png"
            alt=""
            className="size-full object-cover rounded-lg"
          />
        </div> */}
        <div className="mt-5">
          <div className="flex flex-col lg:flex-row items-center lg:justify-between mb-5">
            <h3 className="text-xl font-popinsSemiBold ">
              Collaboration Details
            </h3>
            {/* Show Add Collaboration button if:
                1. User is the sender (fromNonprofit) - can add tasks to any recipient, OR
                2. User is the recipient and request status is ACCEPTED - can add tasks to their own recipient record
            */}
            {requestId &&
              recipient &&
              ((fromNonprofit && currentUserNonprofitId === fromNonprofit.id) ||
                (recipient?.nonprofit?.id === currentUserNonprofitId &&
                  recipient?.status === "ACCEPTED")) && (
                <button
                  className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-sm mt-5 rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] active:scale-95 active:shadow-inner focus:outline-none flex items-center px-5 gap-x-2"
                  onClick={() => setIsOpen(true)}
                >
                  <span className="bg-white p-1 rounded-md">
                    <Plus
                      size={14}
                      strokeWidth={3}
                      className="text-themeColor"
                    />
                  </span>
                  <span>Add Collaboration</span>
                  <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
                </button>
              )}
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-300 panelScroll mt-5">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                    Resource
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                    Assign To
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm"
                    >
                      No tasks assigned yet.
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr className="border-t" key={task.id}>
                      <td className="px-4 py-4 text-sm font-popinsRegular">
                        {task.taskName}
                      </td>
                      <td className="px-4 py-4 text-sm font-popinsRegular">
                        {task.countNeeded || "—"}
                      </td>
                      <td className="px-4 py-4 text-sm font-popinsRegular text-black/60">
                        {task.assigneeOrganizationName ||
                          recipient?.nonprofit?.organizationName ||
                          "N/A"}
                      </td>
                      <td className="px-4 py-4 font-popinsRegular">
                        <div
                          className={`${
                            recipient?.status === "ACCEPTED"
                              ? "bg-[#3FE36B]"
                              : recipient?.status === "REJECTED"
                                ? "bg-red-500"
                                : task.status === "IN_PROGRESS"
                                  ? "bg-[#3FE36B]"
                                  : task.status === "COMPLETED"
                                    ? "bg-[#3FC4E3]"
                                    : "bg-[#DFCD00]"
                          } flex justify-center rounded-lg px-7 py-2 font-popinsMedium text-sm text-white`}
                        >
                          {recipient?.status === "ACCEPTED"
                            ? "Accepted"
                            : recipient?.status === "REJECTED"
                              ? "Rejected"
                              : task.status === "IN_PROGRESS"
                                ? "In Progress"
                                : task.status === "COMPLETED"
                                  ? "Completed"
                                  : "Pending"}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex gap-x-5 mt-3">
          {/* Only show Save Changes button when navigating from "My Requests" tab */}
          {recipientIdFromQuery && (
            <button
              onClick={handleSaveResources}
              disabled={isSaving}
              className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-sm lg:text-base mt-8 rounded-md shadow-lg
               transform transition duration-300 ease-in-out
               hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
               active:scale-95 active:shadow-inner focus:outline-none px-5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span>{isSaving ? "Saving..." : "Save Changes"}</span>

              <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
            </button>
          )}
          <button
            onClick={() => setIsChatVisible(!isChatVisible)}
            className="relative overflow-hidden py-3 bg-[#F0F5F2] text-black font-popinsSemiBold text-sm lg:text-base mt-8 rounded-md shadow-lg
             transform transition duration-300 ease-in-out
             hover:bg-gary-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
             active:scale-95 active:shadow-inner focus:outline-none px-5"
          >
            <span>{isChatVisible ? "Close Chat" : "Message Partner"}</span>
            <span className="absolute inset-0 rounded-full bg-[#F0F5F2] opacity-0 active:opacity-10 transition-opacity duration-300"></span>
          </button>
        </div>
      </div>
      {isChatVisible && (
        <div className="w-full lg:w-[38%]">
          <Chat
            requestId={requestId}
            recipientId={recipient?.id}
            senderNonprofitId={fromNonprofit?.id}
            recipientNonprofitId={recipient?.nonprofit?.id}
            currentUserNonprofitId={currentUserNonprofitId}
            isVisible={isChatVisible}
            onClose={() => setIsChatVisible(false)}
          />
        </div>
      )}
      {!isChatVisible && (
        <div className="w-full lg:w-[38%]">
          {/* Placeholder or other content when chat is hidden */}
        </div>
      )}
      {/* <div className="mt-6">
          <h4 className="font-popinsSemiBold text-xl">
            Public Listing Preview
          </h4>
          <div className="flex items-center mt-5">
            <div className="w-[50%]">
              {event?.coverImageUrl ? (
                !previewImageError ? (
                  <img
                    src={event.coverImageUrl}
                    alt="Event cover preview"
                    className="w-full h-[150px] object-cover rounded-lg"
                    onError={() => setPreviewImageError(true)}
                  />
                ) : (
                  <div className="w-full h-[150px] bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-center">
                    <p className="text-red-500 text-xs text-center px-4">Image failed to load</p>
                  </div>
                )
              ) : (
                <div className="w-full h-[150px] bg-gray-100 rounded-lg flex items-center justify-center">
                  <p className="text-gray-400 text-sm text-center px-4">No image available</p>
                </div>
              )}
            </div>
            <div className="w-[50%] pl-5">
              <p className="font-popinsMedium text-base">
                {event?.title || "Event Title"}
              </p>
              <p className="font-popinsRegular text-sm">
                {(() => {
                  const categoryValue = event?.categoryValue || event?.typeCode;
                  if (!categoryValue) return "Category";
                  const category = categories.find(cat => cat.value === categoryValue);
                  return category?.name || event?.typeName || categoryValue || "Category";
                })()}
              </p>
              <p className="font-popinsRegular text-sm text-gray-500">
                {event?.date && event?.time
                  ? (() => {
                      const dateObj = new Date(`${event.date}T${event.time}`);
                      if (!isNaN(dateObj.getTime())) {
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        const year = dateObj.getFullYear();
                        const hours = String(dateObj.getHours()).padStart(2, '0');
                        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                        return `${month}/${day}/${year} ${hours}:${minutes}`;
                      }
                      return "mm/dd/yyyy 00:00";
                    })()
                  : "mm/dd/yyyy 00:00"}
              </p>
              {event?.description && (
                <p className="font-popinsRegular text-sm text-gray-500 mt-2 line-clamp-3">
                  {event.description}
                </p>
              )}
            </div>
          </div>
        </div> */}
      {isOpen && recipient && (
        <AddTask
          onClose={() => setIsOpen(false)}
          requestId={requestId}
          recipientId={recipient.id}
          assigneeName={
            currentUserOrganizationName ||
            recipient?.nonprofit?.organizationName
          }
          assigneeNonprofitId={currentUserNonprofitId}
          categories={categories}
          onSuccess={() => {
            fetchRequestData();
          }}
        />
      )}
    </div>
  );
}
